import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Grid, Popover } from '@mui/material';

import { IOrder } from '../../../types/Order';
import {
  address,
  customer,
  formatDate,
  formatPartyName,
  getWindowDimensions,
  lender,
  transactionType,
} from '../../utils/helpers';
import BackToDashboard from '../BackToDashboard';
import './_base.scss';

interface IData extends IOrder {
  id: string | number;
}

interface IProps {
  data?: IData;
  hideClosingDate?: string;
  hideSigners?: boolean;
}

function OrderAdminDetails(props: IProps) {
  const { data, hideClosingDate, hideSigners } = props;
  const gridColumnNumber = hideSigners ? 3 : 2.4;
  const detailsPadding = getWindowDimensions().width > 900 ? 0 : 2;
  const fontSize = getWindowDimensions().width > 900 ? 16 : 14;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'more-popover' : undefined;
  const navigate = useNavigate();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.target as HTMLDivElement);
  };

  const getStatusStyle = () => {
    let className = 'item-status';
    let statusType = 'success';
    const statusValue = data?.Status ? data?.Status : 'Open';

    if (statusValue.toLowerCase().includes('failed')) {
      statusType = 'failed';
    }

    if (statusValue.toLowerCase().includes('archived')) {
      statusType = 'archived';
    }

    if (statusValue.toLowerCase().includes('canceled')) {
      statusType = 'canceled';
    }

    switch (statusType) {
      case 'success':
        className += ' success';
        break;
      case 'failed':
        className += ' failed';
        break;
      case 'archived':
        className += ' archived';
        break;
      case 'canceled':
        className += ' canceled';
        break;
      default:
        className += ' success';
    }
    return className;
  };

  return (
    <>
      <div className='order-admin-details'>
        {getWindowDimensions().width < 1000 && (
          <>
            <Grid
              className='order-detail-items'
              container
              sx={{ padding: '10px' }}>
              <Grid item xs={6}>
                <BackToDashboard backClicked={() => navigate('/')} />
              </Grid>
              <Grid className='order-detail-item' item xs={6}>
                <Grid
                  container
                  sx={{
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                    display: 'flex',
                    color: (theme) => theme.palette.primary.main,
                  }}>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      justifyContent: 'flex-end',
                      alignItems: 'flex-end',
                      display: 'flex',
                    }}>
                    Status:
                  </Grid>
                  <Grid
                    item
                    className='order-detail-item-value'
                    flexShrink={1}
                    flexGrow={0}
                    pl={1}
                    sx={{ alignContent: 'baseline' }}>
                    <div className={getStatusStyle()}>{data?.Status}</div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        <Grid
          container
          className='title-bar'
          sx={{ marginBottom: '0 !important' }}>
          <Grid className='order-detail-item' item xs={6}>
            Order Details
          </Grid>
        </Grid>
        <Grid
          className='order-detail-items'
          container
          sx={{ height: 180, alignItems: 'center' }}>
          <Grid
            item
            xs={12}
            sx={{ paddingLeft: detailsPadding, paddingRight: detailsPadding }}>
            <Grid container sx={{ height: 75 }}>
              {getWindowDimensions().width > 900 && (
                <Grid
                  className='order-detail-item'
                  item
                  xs={1.5}
                  sx={{ alignItems: 'left', paddingLeft: 3 }}>
                  <div className='column-label' style={{ fontSize: fontSize }}>
                    Status
                  </div>
                  <div className='order-detail-item-value'>
                    <div className={getStatusStyle()}>{data?.Status}</div>
                  </div>
                </Grid>
              )}
              <Grid className='order-detail-item' item xs={1.5}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  Region
                </div>
                <div
                  className='order-detail-item-value'
                  style={{ fontSize: fontSize }}>
                  {data?.Region?.Name}
                </div>
              </Grid>
              {data?.FileNumber && (
                <Grid className='order-detail-item' item xs={gridColumnNumber}>
                  <div className='column-label' style={{ fontSize: fontSize }}>
                    File Number
                  </div>
                  <div
                    className='order-detail-item-value'
                    style={{ fontSize: fontSize }}>
                    {data?.FileNumber}
                  </div>
                </Grid>
              )}
              <Grid className='order-detail-item' item xs={gridColumnNumber}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  Order Number
                </div>
                <div
                  className='order-detail-item-value'
                  style={{ fontSize: fontSize }}>
                  {data?.id}
                </div>
              </Grid>
              {data?.CreatedAt && (
                <Grid
                  className='order-detail-item'
                  item
                  xs={gridColumnNumber}
                  sx={{ marginTop: 2 }}>
                  <div className='column-label' style={{ fontSize: fontSize }}>
                    Placed By
                  </div>
                  <div
                    className='order-detail-item-value'
                    style={{ fontSize: fontSize }}>
                    {data?.CreatedBy?.split('::')[0]}
                    <div className='sub-text'>
                      {formatDate(data?.CreatedAt, 'ddd, MMM Do YYYY, h:mm A')}
                    </div>
                  </div>
                </Grid>
              )}
              {!hideSigners && (
                <Grid
                  className='order-detail-item'
                  item
                  xs={gridColumnNumber}
                  sx={{ marginTop: 2 }}>
                  <div className='column-label' style={{ fontSize: fontSize }}>
                    customer name
                  </div>
                  <div
                    className='order-detail-item-value'
                    style={{ fontSize: fontSize }}>
                    {customer(data)}
                    {data?.OrderDetails?.Signers &&
                      data?.OrderDetails?.Signers.length > 1 && (
                        <div>
                          <div
                            className='hyper-link more-signers'
                            onClick={(e) => handleOpen(e)}>
                            {'+ ' +
                              (Number(data?.OrderDetails.Signers.length) - 1) +
                              ' More'}
                          </div>
                          <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}>
                            {data?.OrderDetails.Signers.map(
                              (signer, index) =>
                                index !== 0 && (
                                  <div className='show-more-item' key={index}>
                                    {formatPartyName(
                                      {
                                        FirstName: signer.FirstName,
                                        LastName: signer.LastName,
                                        MiddleName: signer.MiddleName,
                                        Suffix: signer.Suffix,
                                      },
                                      'signer'
                                    )}
                                    <div className='show-more-item-subtext'>{`${signer.SignerType} ${signer.PrincipalType}`}</div>
                                  </div>
                                )
                            )}
                          </Popover>
                        </div>
                      )}
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid container sx={{ height: 75 }}>
              <Grid
                className='order-detail-item'
                item
                xs={1.5}
                sx={{ alignItems: 'left', paddingLeft: 3 }}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  Order Type
                </div>
                <div className='order-detail-item-value'>
                  <div>{data?.OrderType?.Name}</div>
                </div>
              </Grid>
              <Grid className='order-detail-item' item xs={1.5}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  lender
                </div>
                <div
                  className='order-detail-item-value'
                  style={{ fontSize: fontSize }}>
                  {lender(data)}
                </div>
              </Grid>
              <Grid className='order-detail-item' item xs={gridColumnNumber}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  property address
                </div>
                <div
                  className='order-detail-item-value'
                  style={{ fontSize: fontSize }}>
                  {address(data)}
                </div>
              </Grid>
              {!hideClosingDate &&
                data?.OrderDetails &&
                data?.OrderDetails?.SigningDate && (
                  <Grid
                    className='order-detail-item'
                    item
                    xs={gridColumnNumber}>
                    {data?.OrderDetails?.NotarizeSigningWindowEndDate ? (
                      <>
                        <div className='column-label'>closing window</div>
                        <div className='order-detail-item-value'>
                          {formatDate(
                            data?.OrderDetails?.SigningDate,
                            'ddd, MMM Do YYYY',
                            data?.OrderDetails?.SigningDateTimeZone
                          )}
                        </div>
                        <div className='order-detail-item-value'>
                          {formatDate(
                            data?.OrderDetails?.NotarizeSigningWindowEndDate,
                            'ddd, MMM Do YYYY',
                            data?.OrderDetails?.SigningDateTimeZone
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className='column-label'>closing date</div>
                        <div className='order-detail-item-value'>
                          {formatDate(
                            data?.OrderDetails?.SigningDate,
                            'ddd, MMM Do YYYY',
                            data?.OrderDetails?.SigningDateTimeZone
                          )}
                        </div>
                      </>
                    )}
                  </Grid>
                )}
              <Grid className='order-detail-item' item xs={gridColumnNumber}>
                <div className='column-label' style={{ fontSize: fontSize }}>
                  Transaction Type
                </div>
                <div
                  className='order-detail-item-value'
                  style={{ fontSize: fontSize }}>
                  {transactionType(data)}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

export default OrderAdminDetails;
